import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  TextField,
  Button,
  Box,
  Stack,
  MenuItem,
  Autocomplete,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import type { ContractStatus, UpdateUserData } from '@/types'
import { useEnumsStore } from '@/stores/enumsStore'

const createContractFormSchema = (t: (key: string) => string) => z.object({
  surname: z.string().max(255).optional(),
  first_name: z.string().max(255).optional(),
  middle_name: z.string().max(255).nullable().optional(),
  email: z.string().email().max(255).optional(),
  birthday: z.string().nullable().optional(),
  nickname: z.string().max(255).nullable().optional(),
  contract_status: z
    .enum(['active', 'suspended', 'completed', 'cancelled'])
    .optional(),
  contract_group_id: z
    .number()
    .nullable()
    .optional(),
  contract_starts_at: z
    .string()
    .optional(),
  contract_expires_at: z
    .string()
    .nullable()
    .optional(),
  contract_notes: z
    .string()
    .max(1000, t('contracts.validation.notesMaxLength'))
    .nullable()
    .optional()
    .or(z.literal('')),
})

interface ContractFormProps {
  initialValues?: {
    surname?: string
    first_name?: string
    middle_name?: string | null
    email?: string
    birthday?: string | null
    nickname?: string | null
    contract_status?: ContractStatus
    contract_group_id?: number | null
    contract_starts_at?: string
    contract_expires_at?: string | null
    contract_notes?: string | null
  }
  /** Если true, показываются поля личных данных ученика (ФИО, email, день рождения, никнейм) */
  showUserFields?: boolean
  onSubmit: (data: UpdateUserData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
  availableGroups?: Array<{ id: number; name: string }>
}

export function ContractForm({
  initialValues,
  showUserFields = false,
  onSubmit,
  onCancel,
  isLoading,
  availableGroups = [],
}: ContractFormProps) {
  const { t, i18n } = useTranslation()
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'
  const enums = useEnumsStore((state) => state.enums)
  const contractStatusOptions = enums?.ContractStatus ?? []

  const defaultContract = {
    contract_status: 'active' as const,
    contract_group_id: null as number | null,
    contract_starts_at: '',
    contract_expires_at: null as string | null,
    contract_notes: '',
  }
  const defaultUser = {
    surname: '',
    first_name: '',
    middle_name: '',
    email: '',
    birthday: '',
    nickname: '',
  }

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateUserData>({
    resolver: zodResolver(createContractFormSchema(t)),
    defaultValues: {
      ...defaultUser,
      ...defaultContract,
      ...initialValues,
      middle_name: initialValues?.middle_name ?? '',
      nickname: initialValues?.nickname ?? '',
      birthday: initialValues?.birthday ?? '',
    },
  })

  const handleFormSubmit = async (data: UpdateUserData) => {
    const payload: UpdateUserData = showUserFields
      ? data
      : {
          contract_status: data.contract_status,
          contract_group_id: data.contract_group_id,
          contract_starts_at: data.contract_starts_at,
          contract_expires_at: data.contract_expires_at,
          contract_notes: data.contract_notes,
        }
    await onSubmit(payload)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {showUserFields && (
            <>
              <Controller
                name="surname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('auth.surname')}
                    error={!!errors.surname}
                    helperText={errors.surname?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('auth.firstName')}
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="middle_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('auth.middleName')}
                    error={!!errors.middle_name}
                    helperText={errors.middle_name?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="nickname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('users.nickname')}
                    error={!!errors.nickname}
                    helperText={errors.nickname?.message}
                    fullWidth
                    placeholder={t('users.nicknameHint')}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="email"
                    label={t('common.email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="birthday"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label={t('auth.birthday')}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(date) => field.onChange(date ? date.format('YYYY-MM-DD') : '')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.birthday,
                        helperText: errors.birthday?.message,
                      },
                    }}
                  />
                )}
              />
            </>
          )}

          <Controller
            name="contract_status"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label={t('contracts.status')}
              error={!!errors.contract_status}
              helperText={errors.contract_status?.message}
              fullWidth
            >
              {contractStatusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {availableGroups.length > 0 && (
          <Controller
            name="contract_group_id"
            control={control}
            render={({ field }) => {
              const selectedGroup = field.value != null
                ? availableGroups.find((g) => g.id === field.value) ?? null
                : null
              return (
                <Autocomplete
                  options={availableGroups}
                  getOptionLabel={(opt) => opt.name}
                  value={selectedGroup}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id ?? null)
                  }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('contracts.group')}
                      error={!!errors.contract_group_id}
                      helperText={errors.contract_group_id?.message}
                      placeholder={t('contracts.noGroup')}
                    />
                  )}
                />
              )
            }}
          />
        )}

        <Controller
          name="contract_starts_at"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t('contracts.startsAt')}
              value={field.value ? dayjs(field.value) : null}
              onChange={(date) => field.onChange(date ? date.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.contract_starts_at,
                  helperText: errors.contract_starts_at?.message,
                },
              }}
            />
          )}
        />

        <Controller
          name="contract_expires_at"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t('contracts.expiresAt')}
              value={field.value ? dayjs(field.value) : null}
              onChange={(date) => field.onChange(date ? date.format('YYYY-MM-DD') : null)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.contract_expires_at,
                  helperText: errors.contract_expires_at?.message,
                },
              }}
            />
          )}
        />

        <Controller
          name="contract_notes"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('contracts.notes')}
              placeholder={t('contracts.notesPlaceholder')}
              error={!!errors.contract_notes}
              helperText={errors.contract_notes?.message}
              fullWidth
              multiline
              rows={4}
            />
          )}
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {t('common.save')}
          </Button>
        </Box>
        </Stack>
      </form>
    </LocalizationProvider>
  )
}
